import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronLeft, Plus, Send, ExternalLink, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/* ─────────────────────────────────────────────────────────────────────────────
   ChatDock — a floating messages panel available on every signed-in screen.

   The backend has no live push for direct messages (routes/messages.py calls
   ws_manager.send_to_user, which does not exist), so this polls instead:
   every 6s while a conversation is open, every 45s otherwise for the unread dot.
───────────────────────────────────────────────────────────────────────────── */

const OPEN_POLL_MS = 6000;
const IDLE_POLL_MS = 45000;
const SEEN_KEY = "ivem-chat-seen";

/* IV brand — the same blues the Command Center and the sign-in page use. */
const BRAND = "linear-gradient(135deg,#1b6595 0%,#2480b8 100%)";
const BRAND_SOFT = "linear-gradient(135deg,#1b6595 0%,#3292cc 100%)";

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

function readSeen() {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeSeen(map) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* private mode — the dot just won't persist */
  }
}

export default function ChatDock() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const [people, setPeople] = useState([]);
  const [active, setActive] = useState(null);      // conversation object
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [picking, setPicking] = useState(false);
  const [sending, setSending] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  const endRef = useRef(null);

  const nameById = useMemo(() => {
    const map = {};
    people.forEach((p) => { map[p.user_id] = p.name; });
    if (user?.user_id) map[user.user_id] = user.name;
    return map;
  }, [people, user]);

  const titleOf = useCallback((c) => {
    if (!c) return "";
    if (c.kind === "group") return c.name || "Group";
    const other = (c.participant_ids || []).find((id) => id !== user?.user_id);
    return nameById[other] || "Conversation";
  }, [nameById, user]);

  /* ── data ── */
  const loadConvs = useCallback(async () => {
    try {
      const res = await authAxios().get("/messages/conversations");
      setConvs(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* offline or not permitted — leave the list as it is */
    }
  }, [authAxios]);

  const loadPeople = useCallback(async () => {
    try {
      const res = await authAxios().get("/messages/users");
      setPeople(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPeople([]);
    }
  }, [authAxios]);

  const loadMessages = useCallback(async (convId) => {
    try {
      const res = await authAxios().get(`/messages/conversations/${convId}/messages`);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* keep what is on screen */
    }
  }, [authAxios]);

  /* first load + unread polling */
  useEffect(() => {
    loadConvs();
    loadPeople();
    const id = setInterval(loadConvs, IDLE_POLL_MS);
    return () => clearInterval(id);
  }, [loadConvs, loadPeople]);

  /* open conversation polling */
  useEffect(() => {
    if (!open || !active) return undefined;
    loadMessages(active.conversation_id);
    const id = setInterval(() => {
      loadMessages(active.conversation_id);
      loadConvs();
    }, OPEN_POLL_MS);
    return () => clearInterval(id);
  }, [open, active, loadMessages, loadConvs]);

  useEffect(() => {
    if (open && active) endRef.current?.scrollIntoView({ block: "end" });
  }, [messages, open, active]);

  /* ── unread ──
     A conversation counts as unread when its newest message landed after
     the last time this browser looked at it. A conversation that has never
     been written in is not unread, whatever its created-at timestamp says. */
  const isUnread = useCallback((c) => {
    if (!c?.last_message_at || !c.last_message_preview) return false;
    const mark = seen[c.conversation_id];
    return !mark || new Date(c.last_message_at) > new Date(mark);
  }, [seen]);

  const unread = convs.filter(isUnread).length;

  /* Badge the installed app icon (taskbar, dock, home screen) with the
     unread count. Only has an effect when IVEM is installed as an app and
     the browser supports it; everywhere else this is a silent no-op.
     Note this updates while the app is running — a badge that changes
     while the app is closed needs Web Push, which the backend has not got. */
  useEffect(() => {
    if (typeof navigator === "undefined" || !("setAppBadge" in navigator)) return;
    try {
      const p = unread > 0 ? navigator.setAppBadge(unread) : navigator.clearAppBadge();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      /* not installed, or the platform does not badge */
    }
  }, [unread]);

  const markSeen = useCallback((id, at) => {
    if (!id) return;
    setSeen((prev) => {
      const stamp = at || new Date().toISOString();
      if (prev[id] && new Date(prev[id]) >= new Date(stamp)) return prev;
      const next = { ...prev, [id]: stamp };
      writeSeen(next);
      return next;
    });
  }, []);

  /* While a conversation is open on screen it is, by definition, read —
     so keep its mark moving as new messages arrive. Without this the badge
     lights up for a thread the person is looking at, and again for every
     message they send themselves. */
  useEffect(() => {
    if (!open || !active) return;
    const live = convs.find((c) => c.conversation_id === active.conversation_id);
    markSeen(active.conversation_id, live?.last_message_at);
  }, [open, active, convs, markSeen]);

  /* ── actions ── */
  const openConversation = (c) => {
    setActive(c);
    setPicking(false);
    markSeen(c.conversation_id, c.last_message_at);
    loadMessages(c.conversation_id);
  };

  const startDm = async (person) => {
    try {
      const res = await authAxios().post("/messages/conversations", {
        kind: "dm",
        participant_ids: [person.user_id],
      });
      setPicking(false);
      await loadConvs();
      openConversation(res.data);
    } catch {
      /* the person may have been removed — refresh the list */
      loadPeople();
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || !active || sending) return;
    setSending(true);
    setDraft("");
    try {
      const res = await authAxios().post(
        `/messages/conversations/${active.conversation_id}/messages`,
        { body },
      );
      setMessages((prev) => [...prev, res.data]);
      loadConvs();
    } catch {
      setDraft(body); // put it back so nothing is lost
    } finally {
      setSending(false);
    }
  };

  /* ── render ── */
  if (!user) return null;

  return (
    <>
      {/* Floating toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close messages" : `Messages${unread ? `, ${unread} unread` : ""}`}
        data-testid="chat-dock-toggle"
        style={{ background: BRAND }}
        className="fixed right-5 bottom-24 app:bottom-6 z-50 flex h-12 items-center gap-2.5 rounded-full py-0 pl-2 pr-4 text-white shadow-lg shadow-[#1b6595]/40 ring-1 ring-white/25 transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {open ? (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
            <X className="h-4 w-4" />
          </span>
        ) : (
          <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_0_0_1px_rgba(255,255,255,0.5)]">
            <img src="/iv-logo.png" alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
          </span>
        )}
        <span className="hidden text-sm font-semibold tracking-tight sm:inline">Messages</span>
        {!open && unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <section
          aria-label="Messages"
          data-testid="chat-dock-panel"
          className="fixed right-5 bottom-40 app:bottom-20 z-50 flex h-[520px] max-h-[70vh] w-[360px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl ring-1 ring-black/5"
        >
          {/* Header — IV brand bar */}
          <header
            style={{ background: BRAND }}
            className="flex h-14 shrink-0 items-center gap-2.5 px-3 text-white"
          >
            {active ? (
              <>
                <button type="button" onClick={() => setActive(null)} aria-label="Back to conversations" className="rounded-md p-1 text-white/80 hover:bg-white/15 hover:text-white">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-[11px] font-bold text-white">
                  {active.kind === "group" ? <Users className="h-4 w-4" /> : initials(titleOf(active))}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold leading-tight">{titleOf(active)}</span>
                  <span className="block truncate text-[10px] uppercase tracking-[0.12em] text-white/60">
                    {active.kind === "group" ? "Group" : "Direct message"}
                  </span>
                </span>
              </>
            ) : (
              <>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_0_0_1px_rgba(255,255,255,0.45)]">
                  <img src="/iv-logo.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold leading-tight">Ismaili Volunteers</span>
                  <span className="block truncate text-[10px] uppercase tracking-[0.14em] text-white/60">Southwest · Messages</span>
                </span>
                <button
                  type="button"
                  onClick={() => { setPicking((v) => !v); loadPeople(); }}
                  aria-label="Start a new conversation"
                  className="ml-auto rounded-md p-1 text-white/80 hover:bg-white/15 hover:text-white"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { setOpen(false); navigate("/messages"); }}
                  aria-label="Open the full Messages page"
                  title="Open full page"
                  className="rounded-md p-1 text-white/80 hover:bg-white/15 hover:text-white"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close messages"
              className={`rounded-md p-1 text-white/80 hover:bg-white/15 hover:text-white ${active ? "ml-auto" : ""}`}
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {/* Body */}
          {!active ? (
            <div className="flex-1 overflow-y-auto">
              {picking ? (
                people.length === 0 ? (
                  <p className="p-4 text-center text-xs text-muted-foreground">Nobody else has an account yet.</p>
                ) : (
                  people.map((p) => (
                    <button
                      key={p.user_id}
                      type="button"
                      onClick={() => startDm(p)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted"
                    >
                      <span style={{ background: BRAND_SOFT }} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white">
                        {initials(p.name)}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-foreground">{p.name}</span>
                        <span className="block truncate text-xs capitalize text-muted-foreground">{(p.role || "").replace(/_/g, " ")}</span>
                      </span>
                    </button>
                  ))
                )
              ) : convs.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
                  <img src="/iv-logo.png" alt="" aria-hidden="true" className="h-12 w-12 object-contain opacity-30" />
                  <p className="text-sm text-muted-foreground">No conversations yet.</p>
                  <button type="button" onClick={() => { setPicking(true); loadPeople(); }} className="text-sm font-semibold text-primary hover:underline">
                    Start one
                  </button>
                </div>
              ) : (
                convs.map((c) => {
                  const unreadRow = isUnread(c);
                  return (
                    <button
                      key={c.conversation_id}
                      type="button"
                      onClick={() => openConversation(c)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted"
                    >
                      <span style={{ background: BRAND_SOFT }} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white">
                        {c.kind === "group" ? <Users className="h-4 w-4" /> : initials(titleOf(c))}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className={`truncate text-sm ${unreadRow ? "font-bold text-foreground" : "font-medium text-foreground"}`}>{titleOf(c)}</span>
                          <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{timeAgo(c.last_message_at)}</span>
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {c.last_message_preview || "No messages yet"}
                        </span>
                      </span>
                      {unreadRow && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                    </button>
                  );
                })
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {messages.length === 0 ? (
                  <p className="py-8 text-center text-xs text-muted-foreground">No messages yet. Say hello.</p>
                ) : (
                  messages.map((m) => {
                    const mine = m.author_id === user?.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          style={mine ? { background: BRAND } : undefined}
                          className={`max-w-[80%] px-3 py-2 ${mine ? "rounded-2xl rounded-br-md text-white" : "rounded-2xl rounded-bl-md bg-muted text-foreground"}`}
                        >
                          {!mine && active.kind === "group" && (
                            <p className="mb-0.5 text-[10px] font-semibold opacity-70">{m.author_name}</p>
                          )}
                          <p className="whitespace-pre-wrap break-words text-sm leading-snug">{m.body}</p>
                          <p className={`mt-1 text-[10px] ${mine ? "text-white/60" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={endRef} />
              </div>

              <form onSubmit={send} className="flex shrink-0 items-center gap-2 border-t border-border p-2">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Write a message…"
                  aria-label="Message"
                  data-testid="chat-dock-input"
                  className="min-w-0 flex-1 rounded-full border border-input bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
                />
                <button
                  type="submit"
                  disabled={!draft.trim() || sending}
                  aria-label="Send"
                  style={{ background: BRAND }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-40"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </section>
      )}
    </>
  );
}
